/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    getCompetitions,
    createCompetition,
    updateCompetition,
    createOrUpdateCompetitionWinner,
    deleteCompetitionWinner,
    getCompetitionReports,
} from './competitionApi';
import { logoutUser } from '../user/userApi';
import { Competition, CompetitionFilters, CompetitionReport } from './types';

export interface CompetitionState {
    error: any;
    isFetching: boolean;
    competitions: Competition[];
    currentCompetition: Competition | null;
    filters: CompetitionFilters;
    reports: CompetitionReport | null;
    isReportsFetching: boolean;
    date_from: string | null;
    date_to: string | null;
}

const initialState: CompetitionState = {
    error: null,
    isFetching: false,
    competitions: [],
    currentCompetition: null,
    filters: {},
    reports: null,
    isReportsFetching: false,
    date_from: null,
    date_to: null,
};

export const competitionSlice = createSlice({
    name: 'competition',
    initialState,
    reducers: {
        setCurrentCompetition: (state, action: PayloadAction<Competition | null>) => {
            state.currentCompetition = action.payload;
        },
        setFilters: (state, action: PayloadAction<CompetitionFilters>) => {
            state.filters = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearCompetitions: (state) => {
            state.competitions = [];
            state.currentCompetition = null;
        },
        loadSingleCompetition: (state, action: PayloadAction<number>) => {
            const competition = state.competitions.find(c => c.id === action.payload) || null;
            state.currentCompetition = competition;
        },
        resetSingleCompetition: (state) => {
            state.currentCompetition = null;
        },
        clearReports: (state) => {
            state.reports = null;
        },
    },
    extraReducers: (builder) => {
        // Get competitions
        builder.addCase(getCompetitions.fulfilled, (state: CompetitionState, action) => {
            state.isFetching = false;
            state.competitions = action?.payload?.competitions || [];
        });

        // Create competition
        builder.addCase(createCompetition.fulfilled, (state: CompetitionState, action) => {
            state.isFetching = false;
            const newCompetition = action?.payload?.competition;

            // Add to competitions array
            if (newCompetition) {
                state.competitions.push(newCompetition);
                state.currentCompetition = newCompetition;
            }

            notification.success({
                message: 'Success',
                description: 'Konkursas sėkmingai sukurtas',
            });
        });

        // Update competition
        builder.addCase(updateCompetition.fulfilled, (state: CompetitionState, action) => {
            state.isFetching = false;
            const updatedCompetition = action?.payload?.competition;

            // Update currentCompetition
            state.currentCompetition = updatedCompetition;

            // Update competitions array
            const index = state.competitions.findIndex(c => c.id === updatedCompetition.id);
            if (index !== -1) {
                state.competitions[index] = updatedCompetition;
            }

            notification.success({
                message: 'Success',
                description: 'Konkursas sėkmingai atnaujintas',
            });
        });

        // Create or update competition winner
        builder.addCase(createOrUpdateCompetitionWinner.fulfilled, (state: CompetitionState, action) => {
            state.isFetching = false;
            const updatedCompetition = action?.payload?.competition;

            if (updatedCompetition) {
                // Update currentCompetition
                state.currentCompetition = updatedCompetition;

                // Update competitions array
                const index = state.competitions.findIndex(c => c.id === updatedCompetition.id);
                if (index !== -1) {
                    state.competitions[index] = updatedCompetition;
                }
            }
        });

        // Delete competition winner
        builder.addCase(deleteCompetitionWinner.fulfilled, (state: CompetitionState, action) => {
            state.isFetching = false;
            const updatedCompetition = action?.payload?.competition;

            if (updatedCompetition) {
                // Update currentCompetition
                state.currentCompetition = updatedCompetition;

                // Update competitions array
                const index = state.competitions.findIndex(c => c.id === updatedCompetition.id);
                if (index !== -1) {
                    state.competitions[index] = updatedCompetition;
                }
            }
        });

        // Get competition reports
        builder.addCase(getCompetitionReports.fulfilled, (state: CompetitionState, action) => {
            state.isReportsFetching = false;
            state.reports = action?.payload?.report || null;
            state.date_from = action?.payload?.date_from || null;
            state.date_to = action?.payload?.date_to || null;
        });

        // on logout
        builder.addCase(logoutUser.fulfilled, (state: CompetitionState) => {
            state.competitions = [];
            state.currentCompetition = null;
            state.reports = null;
        });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                createCompetition.pending,
                updateCompetition.pending,
                createOrUpdateCompetitionWinner.pending,
                deleteCompetitionWinner.pending,
            ),
            (state: CompetitionState) => {
                state.isFetching = true;
                state.error = null;
            },
        );

        // Reports pending state
        builder.addMatcher(
            isAnyOf(
                getCompetitionReports.pending,
            ),
            (state: CompetitionState) => {
                state.isReportsFetching = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getCompetitions.pending,
            ),
            (state: CompetitionState) => {
                state.isFetching = true;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                getCompetitions.rejected,
                createCompetition.rejected,
                updateCompetition.rejected,
                createOrUpdateCompetitionWinner.rejected,
                deleteCompetitionWinner.rejected,
                getCompetitionReports.rejected,
            ),
            (state: CompetitionState, action) => {
                state.isFetching = false;
                state.isReportsFetching = false;
                state.error = action.error;
            },
        );
    },
});

export const {
    setCurrentCompetition,
    setFilters,
    clearError,
    clearCompetitions,
    loadSingleCompetition,
    resetSingleCompetition,
    clearReports,
} = competitionSlice.actions;

export default competitionSlice.reducer;
